import { useState, useEffect, Fragment } from 'react';
import {
  KeyIcon
} from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-toastify';

import SystemService from '../../services/system.service';
import UserAdminService from '../../services/user.admin.service';
import Spinner from '../../components/ui/spinner.component';

export default function PinsView() {
  const [setPinModalOpen, setSetPinModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);

  useEffect(() => {
    // Load admin users for dropdown
    loadAdminUsers();
    document.title = "Assemblo - Admin - PIN beheer";
  }, []);

  async function loadAdminUsers() {
    try {
      const response = await UserAdminService.getUsers({
        page: 1,
        per_page: 100,
        filter: 'admins'
      });
      // Extract users from pagination response
      setAdminUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading admin users:', error);
      toast.error('Fout bij het laden van administrators');
    }
  }

  function openSetPinModal() {
    setSetPinModalOpen(true);
  }

  function closeSetPinModal() {
    setSetPinModalOpen(false);
    setPassword('');
    setNewPin('');
    setSelectedUserId('');
  }

  async function handleSetPin() {
    if (!password) {
      toast.error('Voer een wachtwoord in');
      return;
    }

    if (!selectedUserId) {
      toast.error('Selecteer een gebruiker');
      return;
    }

    if (!newPin) {
      toast.error('Voer een PIN in');
      return;
    }

    if (!/^\d{6}$/.test(newPin)) {
      toast.error('PIN moet precies 6 cijfers zijn');
      return;
    }

    // Check for weak PINs
    const commonPins = [
      '000000', '111111', '222222', '333333', '444444', '555555',
      '666666', '777777', '888888', '999999', '123456', '654321',
      '123123', '121212', '696969', '112233', '102030'
    ];

    if (commonPins.includes(newPin)) {
      toast.error('PIN is te zwak. Vermijd veelgebruikte codes zoals 123456 of 000000');
      return;
    }

    // Check if all digits are the same
    if (new Set(newPin).size === 1) {
      toast.error('PIN is te zwak. Alle cijfers zijn hetzelfde');
      return;
    }

    // Check for ascending sequential (012345, 123456, etc.)
    const isAscending = newPin.split('').every((digit, i) => {
      if (i === 0) return true;
      return parseInt(digit) === parseInt(newPin[i - 1]) + 1;
    });

    if (isAscending) {
      toast.error('PIN is te zwak. Vermijd opeenvolgende cijfers');
      return;
    }

    // Check for descending sequential (543210, 654321, etc.)
    const isDescending = newPin.split('').every((digit, i) => {
      if (i === 0) return true;
      return parseInt(digit) === parseInt(newPin[i - 1]) - 1;
    });

    if (isDescending) {
      toast.error('PIN is te zwak. Vermijd opeenvolgende cijfers');
      return;
    }

    setLoading(true);

    try {
      await SystemService.setPinForAdmin(password, selectedUserId, newPin);
      toast.success('PIN succesvol ingesteld');
      closeSetPinModal();
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error('Ongeldig wachtwoord of geen admin gebruiker');
      } else if (error.response?.status === 404) {
        toast.error('Gebruiker niet gevonden');
      } else {
        toast.error('Er is een fout opgetreden: ' + (error.response?.data || error.message));
      }
    } finally {
      setLoading(false);
      setPassword('');
    }
  }

  return (
    <>
      <main className="flex-1">
        <div className="py-6">
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl font-semibold text-gray-900">PIN beheer</h1>
            <p className="mt-1 text-sm text-gray-500">
              Stel PIN codes in voor administrators
            </p>
          </div>

          {/* Set PIN Card */}
          <div className="mx-auto px-4 sm:px-6 md:px-8 mt-8">
            <div className="max-w-2xl">
              <div className="bg-white shadow rounded-lg px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold leading-6 text-gray-900">PIN instellen</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Stel een nieuwe PIN in voor een administrator. De PIN wordt veilig opgeslagen als hash.
                    </p>
                  </div>
                  <KeyIcon className="h-6 w-6 text-gray-400 flex-shrink-0 ml-4" />
                </div>

                <button
                  type="button"
                  onClick={openSetPinModal}
                  disabled={loading}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  PIN instellen
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Set PIN Modal */}
      <Transition.Root show={setPinModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeSetPinModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                      <KeyIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        PIN instellen voor administrator
                      </Dialog.Title>
                      <div className="mt-4 space-y-4">
                        <div className="text-left">
                          <label htmlFor="admin-select" className="block text-sm font-medium text-gray-700 mb-1">
                            Selecteer administrator
                          </label>
                          <select
                            id="admin-select"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          >
                            <option value="">-- Kies een administrator --</option>
                            {adminUsers.map((admin) => (
                              <option key={admin.id} value={admin.id}>
                                {admin.firstname} {admin.lastname} ({admin.email})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="text-left">
                          <label htmlFor="pin-input" className="block text-sm font-medium text-gray-700 mb-1">
                            PIN (6 cijfers)
                          </label>
                          <input
                            id="pin-input"
                            type="text"
                            value={newPin}
                            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="123456"
                            maxLength={6}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 font-mono"
                          />
                        </div>

                        <div className="text-left">
                          <label htmlFor="password-input" className="block text-sm font-medium text-gray-700 mb-1">
                            Administrator wachtwoord
                          </label>
                          <input
                            id="password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSetPin()}
                            placeholder="Wachtwoord"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 disabled:opacity-50"
                      onClick={handleSetPin}
                      disabled={loading}
                    >
                      PIN instellen
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={closeSetPinModal}
                    >
                      Annuleren
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Loading Modal - Unclosable */}
      <Transition.Root show={loading} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-5 text-center shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-8">
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      Bezig met laden...
                    </Dialog.Title>
                    <div className="py-4">
                      <Spinner size="large" />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
